import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Radio, RadioGroup,
  FormControlLabel, FormControl, Checkbox, Grid, IconButton, Divider,
  InputAdornment, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip, Fade, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Select, MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon, Save as SaveIcon, Clear as ClearIcon,
  CloudDownload, Refresh as RefreshIcon, Add as AddIcon, Close as CloseIcon,
  HealthAndSafety as InsuranceIcon, LocalHospital as DiagnosisIcon,
  CheckCircle as CheckCircleIcon, Delete as DeleteIcon, ContentCopy as CopyIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import {
  loadInsurance, loadDiagnoses, getDiagnosisInsurances,
  updateInsuredDiagnosis, deleteInsuredDiagnosis, saveNewDiagnosis,
  bulkApplyDiagnosisInsurance,
  bulkUpdateInsuredDiagnosis, bulkDeleteInsuredDiagnosis,
  diagnosisFieldOptions,
} from '../BaseURL';

import ApplyToManyBar from './ApplyToManyBar';
import LookupField from './LookupField';

/* Fields offered by the "Apply to many" toolbar — blank ones are kept */
const APPLY_TO_MANY_FIELDS = [
  { key: 'tariff',   label: 'Tariff',         type: 'money'                                                },
  { key: 'topup',    label: 'Top-up',         type: 'money'                                                },
  { key: 'age_cat',  label: 'Age',            type: 'select', options: ['Adult', 'Child', 'Both'], width: 130 },
  { key: 'icd',      label: 'ICD-10',         type: 'text',   placeholder: 'e.g. I50.1'                    },
  { key: 'core',     label: 'Core / GDRG',    type: 'text',   placeholder: 'e.g. MEDI07A'                   },
  { key: 'spe',      label: 'Specialty',      type: 'text'                                                 },
  { key: 'spe_code', label: 'Specialty Code', type: 'text'                                                 },
];

/* ─────────────────────────────────────────────────────────────
   Shared compact styles
───────────────────────────────────────────────────────────── */
const cellSelectSx = {
  fontSize: '0.78rem',
  '& .MuiSelect-select': { py: '4px', px: '8px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
};
const cellInputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.78rem',
    '& input': { py: '4px', px: '8px' },
    '& fieldset': { borderColor: '#e0e0e0' },
    '&:hover fieldset': { borderColor: '#667eea' },
    '&.Mui-focused fieldset': { borderColor: '#667eea' },
  },
};

/* ─────────────────────────────────────────────────────────────
   Editable table row with checkbox selection
───────────────────────────────────────────────────────────── */
const EditableRow = ({ row, idx, selected, onSelectToggle, onChange, onUpdate, onDelete, updating, deleting }) => {
  const tdSx = { borderBottom: '1px solid #f0f0f0', py: 0.75, px: 1 };
  return (
    <TableRow sx={{
      '&:nth-of-type(odd)': { background: selected ? 'rgba(102,126,234,0.12)' : '#f9faff' },
      '&:nth-of-type(even)': { background: selected ? 'rgba(102,126,234,0.12)' : '#fff' },
      '&:hover': { background: selected ? 'rgba(102,126,234,0.18)' : 'rgba(102,126,234,0.04)' },
      transition: 'background 0.2s',
    }}>
      <TableCell sx={{ ...tdSx, width: 48, textAlign: 'center', px: 0.5 }}>
        <Checkbox 
          size="small"
          checked={selected}
          onChange={() => onSelectToggle(row.id)}
          sx={{ 
            color: '#667eea', 
            '&.Mui-checked': { color: '#667eea' },
            p: 0.5
          }}
        />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 36, textAlign: 'center', color: '#9e9e9e', fontSize: '0.72rem', fontWeight: 500 }}>{idx + 1}</TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 130 }}>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1a237e' }}>{row.status || '—'}</Typography>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <TextField size="small" type="number" fullWidth value={row.tariff ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('tariff', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 95 }}>
        <TextField size="small" type="number" fullWidth value={row.topup ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('topup', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <Select size="small" fullWidth value={row.age_cat || ''} onChange={e => onChange('age_cat', e.target.value)} displayEmpty sx={cellSelectSx}>
          <MenuItem value="" disabled><em style={{ color: '#bbb', fontSize: '0.75rem' }}>Select…</em></MenuItem>
          <MenuItem value="Adult">Adult</MenuItem>
          <MenuItem value="Child">Child</MenuItem>
          <MenuItem value="Both">Both</MenuItem>
        </Select>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 110 }}>
        <TextField size="small" fullWidth value={row.icd ?? ''} onChange={e => onChange('icd', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" fullWidth value={row.core ?? ''} onChange={e => onChange('core', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 120 }}>
        <TextField size="small" fullWidth value={row.spe ?? ''} onChange={e => onChange('spe', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" fullWidth value={row.spe_code ?? ''} onChange={e => onChange('spe_code', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 180, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
          <Button size="small" variant="contained" onClick={() => onUpdate(row)} disabled={updating || deleting}
            startIcon={updating ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            sx={{ fontSize: '0.7rem', px: 1.5, py: 0.5, background: 'linear-gradient(90deg,#11998e,#11998e)', color: 'white', fontWeight: 600, textTransform: 'none', boxShadow: '0 2px 8px rgba(17,153,142,0.25)', '&:hover': { background: 'linear-gradient(90deg,#0e8a7f,#2fd469)' }, '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' } }}>
            {updating ? 'Saving...' : 'Update'}
          </Button>
          <Button size="small" variant="contained" onClick={() => onDelete(row)} disabled={deleting || updating}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            sx={{ fontSize: '0.7rem', px: 1.5, py: 0.5, background: 'linear-gradient(90deg,#e53935,#e53935)', color: 'white', fontWeight: 600, textTransform: 'none', boxShadow: '0 2px 8px rgba(229,57,53,0.25)', '&:hover': { background: 'linear-gradient(90deg,#c62828,#ef5350)' }, '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' } }}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
};

/* ─────────────────────────────────────────────────────────────
   Bulk Apply Dialog - Apply selected row to multiple insurances
───────────────────────────────────────────────────────────── */
/*  ── AGE IS PART OF THE IDENTITY, AND THE CODE IS PER SCHEME ─────────────────
 *
 *  The same diagnosis exists twice under one scheme — once for an adult, once
 *  for a child — because the G-DRG differs. "Bacteria Enteritis" is MEDI23A for
 *  an adult and a PAED code for a child.
 *
 *  This dialog could express neither. It copied the SOURCE row's age and the
 *  SOURCE row's code to every scheme, and it hid any scheme that already held
 *  the diagnosis under "Already Assigned" — matching on the scheme NAME alone.
 *  So once the adult row existed there was no way to add the child one, and no
 *  way to give a scheme its own code.
 *
 *  Two changes:
 *    · an age selector, which re-partitions Available / Already Assigned, so
 *      the same scheme is available again for the age it does not yet have
 *    · a per-scheme code box, prefilled from the source and editable
 *
 *  `existingRows` replaces `existingStatuses`: the parent now passes
 *  { status, age_cat } so this can match on both. A caller that still passes
 *  only names keeps working — the age half of the comparison just never matches.
 */
const AGE_CATEGORIES = ['Adult', 'Child', 'Both'];

const BulkApplyDialog = ({ open, onClose, sourceRow, insuranceProviders, loadingInsurance, onSaved, existingRows = [] }) => {
  const [saving, setSaving] = useState(false);
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');
  //  The age being written. Defaults to the source row's, because copying it
  //  unchanged is the common case — but it is now a choice rather than an
  //  assumption.
  const [ageCat, setAgeCat] = useState('Adult');
  //  The code being written, for every scheme in this apply. Prefilled from the
  //  source row and editable, because the row being REGISTERED usually needs a
  //  different G-DRG from the one being copied — that is the whole reason age is
  //  selectable here. Adult MEDI23A becomes a PAED code for the child row.
  //
  //  It sits at the top of the dialog rather than on the rows, because it applies
  //  to all of them and because a field that only appears after ticking something
  //  is a field nobody finds.
  const [coreCode, setCoreCode] = useState('');
  //  Per-scheme override, keyed by provider id, for the case where one scheme
  //  genuinely differs from the rest. Absent means "use the code above".
  const [codeByProvider, setCodeByProvider] = useState({});

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

  /*  ── NOTHING IS LOCKED OUT ANYMORE ──────────────────────────────────────────
   *
   *  Every scheme stays selectable, whatever it already holds. The server keys a
   *  tariff row on (name, insurance, age) and upserts, so picking a scheme that
   *  already has the row is a legitimate way to CORRECT it — new code, new
   *  tariff — and picking one that does not is an add. Both are wanted.
   *
   *  This used to split the list into Available and "Already has Child", with the
   *  second half disabled. That made the screen the thing preventing the work:
   *  NHIA holding the Child row meant its Adult row could not be created from
   *  here, and a wrong code could not be fixed by re-applying — the row had to be
   *  deleted first.
   *
   *  So the split is gone. What replaces it is a per-row hint saying which
   *  schemes will be UPDATED rather than added, and the existing code, so the
   *  choice is informed instead of forbidden.
   */
  const existingForAge = React.useMemo(() => {
    const wanted = String(ageCat).trim().toLowerCase();
    const map = new Map();
    for (const r of (existingRows || [])) {
      if (String(r?.age_cat ?? '').trim().toLowerCase() !== wanted) continue;
      map.set(String(r?.status ?? '').trim(), String(r?.core ?? '').trim());
    }
    return map;
  }, [existingRows, ageCat]);

  //  How many of the ticked schemes are updates rather than adds — the footer
  //  says so before the button is pressed, not only in the result.
  const updateCount = selectedInsuranceIds.filter(id => {
    const p = insuranceProviders.find(x => String(x.id) === String(id));
    return p && existingForAge.has(String(p.name).trim());
  }).length;

  useEffect(() => {
    if (open) {
      // Reset selections when dialog opens
      setSelectedInsurances({});
      setInsuranceSearch('');
      setCodeByProvider({});
      setCoreCode(String(sourceRow?.core || '').trim());
      setAgeCat(
        AGE_CATEGORIES.includes(String(sourceRow?.age_cat || '').trim())
          ? String(sourceRow.age_cat).trim()
          : 'Adult');
    }
  }, [open, sourceRow]);

  //  Ticks SURVIVE an age change now. Nothing is disabled, so a selection stays
  //  valid whichever age is chosen — and clearing it would throw away the work
  //  of someone who ticked eight schemes and then noticed the age was wrong,
  //  which is the most likely reason to touch that selector at all.
  //
  //  The per-row hints re-evaluate on the age change, so a tick that has become
  //  an update rather than an add says so before Apply is pressed.

  const handleInsuranceToggle = (id) => {
    setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
  };

  //  Everything, including schemes that already hold this age — those become
  //  updates. Skipping them was the old behaviour and it made "Select All" quietly
  //  mean "select all except the ones you probably want to correct".
  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { all[i.id] = true; });
    setSelectedInsurances(all);
  };

  const handleDeselectAll = () => setSelectedInsurances({});

  const handleApply = async () => {
    if (selectedCount === 0) {
      await Swal.fire({
        title: 'No Selection',
        text: 'Please select at least one insurance provider to apply this diagnosis to.',
        icon: 'warning',
        confirmButtonColor: '#11998e',
      });
      return;
    }

    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        //  Three levels, most specific first: this scheme's own override, the
        //  code typed at the top of the dialog, then the source row's.
        //  Emptiness-checked at every level so a box someone cleared falls
        //  through rather than writing a blank code over a real one.
        const typed = String(codeByProvider[id] ?? '').trim()
                   || String(coreCode ?? '').trim();
        return {
          //  The CHOSEN age, not the source row's — this is what lets a child
          //  row be created from an adult one.
          age_cat: ageCat,
          name: sourceRow.name,
          cat: sourceRow.cat,
          icd: sourceRow.icd,
          core: typed || sourceRow.core,
          tariff: parseFloat(sourceRow.tariff),
          topup: parseFloat(sourceRow.topup),
          spe: sourceRow.spe,
          spe_code: sourceRow.spe_code,
          status: provider?.name || ''
        };
      });

      const response = await axios.post(bulkApplyDiagnosisInsurance, { rows });

      if (response.data.success) {
        //  The server's own wording, which distinguishes added from updated.
        //  "applied to 5 providers" hid the difference between creating five
        //  rows and overwriting five that already existed, and those are not the
        //  same thing to whoever answers for the tariffs.
        const added = response.data?.data?.insertedCount ?? 0;
        const changed = response.data?.data?.updatedCount ?? 0;
        await Swal.fire({
          title: changed && !added ? 'Updated!' : 'Applied!',
          html: `<b>${sourceRow.name}</b> · <b>${ageCat}</b><br/>`
              + (added   ? `<span style="color:#2e7d32">${added} added</span>` : '')
              + (added && changed ? ' &middot; ' : '')
              + (changed ? `<span style="color:#ef6c00">${changed} updated</span>` : '')
              + (!added && !changed ? 'Nothing to do.' : ''),
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        if (onSaved) onSaved();
        handleClose();
      } else {
        throw new Error(response.data.message || 'Apply failed');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message
                || 'An error occurred while applying';

      /*  ── THE SERVER HAS NOT BEEN UPDATED YET ─────────────────────────────
       *
       *  The old endpoint keys a duplicate on (diagnosis, insurance) with no age
       *  in it, and REFUSES rather than updating — the behaviour this dialog was
       *  rebuilt to replace. Its message says "deselect these insurance
       *  providers", which is advice the officer cannot act on: the amber hints
       *  on those rows say "will be updated", so the screen promised something
       *  the server then refused.
       *
       *  Recognised by its own wording and answered honestly, naming which half
       *  of the job still works so eighteen ticks do not have to be bisected by
       *  hand. The same branch exists in Procedure.js, against the same server
       *  version and the same message.
       *
       *  Delete once the backend is deployed; it can only fire against a server
       *  that predates it.                                                   */
      if (/already exists for/i.test(msg)) {
        await Swal.fire({
          title: 'This server cannot update yet',
          html: `<div style="text-align:left;font-size:0.9rem;line-height:1.55">`
              + `<p style="margin:0 0 8px">The screen is newer than the server it is `
              + `talking to. This version refuses any insurance that already has `
              + `<b>${sourceRow.name}</b> at all, whatever the age band — so the rows `
              + `marked <b>will be updated</b> cannot be written.</p>`
              + `<p style="margin:0 0 8px"><b>What still works:</b> untick those rows `
              + `and apply to the insurances that do not have this diagnosis. The age `
              + `and the code you chose are saved correctly on those.</p>`
              + `<p style="margin:0;color:#666">The server's own message: `
              + `${msg}</p></div>`,
          icon: 'warning',
          confirmButtonColor: '#11998e',
          confirmButtonText: 'Understood',
        });
      } else {
        await Swal.fire({
          title: 'Apply Failed',
          text: msg,
          icon: 'error',
          confirmButtonColor: '#e53935',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedInsurances({});
    setInsuranceSearch('');
    onClose();
  };

  const filteredProviders = insuranceProviders.filter(i =>
    i.name?.toLowerCase().includes(insuranceSearch.toLowerCase())
  );

  /*  There is no Available / Already-Assigned partition any more.
   *
   *  Two derived lists lived here, rebuilt at one point purely to fix a compile
   *  error after `takenForAge` was removed. The render no longer uses either:
   *  every scheme appears in ONE list and stays selectable, and `existingForAge`
   *  is consulted per row to mark the ones that will be updated rather than
   *  added. Dead code that describes a partition the screen does not have is
   *  worse than no comment, so both are gone. */

  if (!sourceRow) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' } }}>

      <DialogTitle sx={{ background: 'linear-gradient(135deg, #11998e , #11998e)', color: 'white', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CopyIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>Apply to Insurance Providers</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>Select providers to apply this diagnosis</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Source Row Info */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, background: 'linear-gradient(135deg,rgba(102,126,234,0.08),rgba(118,75,162,0.08))', border: '1px solid rgba(102,126,234,0.2)', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3949ab', mb: 1, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>
            Source Diagnosis Details
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                <strong>Name:</strong> {sourceRow.name}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666' }}>
                <strong>ICD:</strong> {sourceRow.icd || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666' }}>
                <strong>Core:</strong> {sourceRow.core || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666' }}>
                <strong>Tariff:</strong> GH¢ {sourceRow.tariff || 0}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666' }}>
                <strong>Top-up:</strong> GH¢ {sourceRow.topup || 0}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666' }}>
                <strong>Age:</strong> {sourceRow.age_cat || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#666' }}>
                <strong>Specialty:</strong> {sourceRow.spe_code || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/*  AGE FIRST, because it is half the identity of the row being written
             and it decides which of the schemes below read as an update rather
             than an add. */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3949ab', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem', mb: 0.75 }}>
            Age Category
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {AGE_CATEGORIES.map(a => (
              <Button key={a} size="small" onClick={() => setAgeCat(a)}
                variant={ageCat === a ? 'contained' : 'outlined'}
                sx={{
                  fontSize: '0.72rem', py: 0.4, minWidth: 74, fontWeight: 700,
                  ...(ageCat === a
                    ? { background: '#11998e', '&:hover': { background: '#0e7f76' } }
                    : { borderColor: '#c5cae9', color: '#5c6bc0' }),
                }}>
                {a}
              </Button>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.5 }}>
            The same diagnosis can exist for <strong>Adult</strong> and <strong>Child</strong> under
            one scheme with different G-DRG codes. Every scheme stays selectable — one that already
            has this age is <strong>updated</strong>, one that does not is <strong>added</strong>.
          </Typography>
        </Box>

        {/*  THE CODE BEING WRITTEN. Prefilled from the source row and editable —
             the row being registered usually needs its own G-DRG, which is the
             point of choosing an age. Name, ICD, tariff and specialty are copied
             from the source unchanged; age and this code are what vary. */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3949ab', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem', mb: 0.75 }}>
            Core / G-DRG Code
          </Typography>
          <TextField
            fullWidth size="small"
            value={coreCode}
            onChange={e => setCoreCode(e.target.value)}
            placeholder={sourceRow.core || 'e.g. PAED33A'}
            inputProps={{ 'aria-label': 'Core / G-DRG code to apply' }}
            sx={{ '& .MuiInputBase-input': { fontWeight: 700, letterSpacing: 0.4 } }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.5 }}>
            {coreCode.trim() && coreCode.trim() !== String(sourceRow.core || '').trim() ? (
              <>Writing <strong>{coreCode.trim()}</strong> instead of the source
              code <strong>{sourceRow.core || '—'}</strong>.</>
            ) : (
              <>Applied to every scheme you tick. Leave as <strong>{sourceRow.core || '—'}</strong> to
              copy the source code, or type the code this age needs.</>
            )}
            {' '}A scheme that differs can be overridden on its own row below.
          </Typography>
        </Box>

        {/* Insurance Selection */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3949ab', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
              Select Insurance Providers
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedCount} of {filteredProviders.length} selected
              {updateCount > 0 && (
                <> · <span style={{ color: '#ef6c00', fontWeight: 700 }}>
                  {updateCount} will be updated
                </span></>
              )}
            </Typography>
          </Box>
          {selectedCount > 0 && (
            <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
              label={`${selectedCount - updateCount} add · ${updateCount} update`}
              sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}
            sx={{ borderColor: '#667eea', color: '#667eea', fontSize: '0.72rem', py: 0.4 }}>
            Select All
          </Button>
          <Button size="small" variant="outlined" onClick={handleDeselectAll}
            sx={{ borderColor: '#667eea', color: '#667eea', fontSize: '0.72rem', py: 0.4 }}>
            Deselect All
          </Button>
        </Box>

        <TextField fullWidth size="small" placeholder="Search insurance..."
          value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)} sx={{ mb: 1.5 }}
          InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} /></InputAdornment>) }} />

        <Box sx={{
          border: '1px solid #e8eaf6', borderRadius: 2, maxHeight: 320, overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { background: '#c5cae9', borderRadius: '10px' },
        }}>
          {loadingInsurance ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {/*  ONE LIST, EVERYTHING SELECTABLE.
                   A scheme that already holds this age is marked and shows the
                   code it currently carries, so re-picking it is an informed
                   correction rather than a blocked action. */}
              {filteredProviders.length > 0 && (
                <Box sx={{ p: 1 }}>
                  {filteredProviders.map(insurance => {
                    const name = String(insurance.name).trim();
                    const isUpdate = existingForAge.has(name);
                    const currentCode = existingForAge.get(name) || '';
                    const ticked = !!selectedInsurances[insurance.id];
                    return (
                      <Box key={insurance.id} onClick={() => handleInsuranceToggle(insurance.id)}
                        sx={{
                          display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1,
                          cursor: 'pointer', mb: 0.5,
                          background: ticked
                            ? (isUpdate ? 'rgba(239,108,0,0.08)' : 'rgba(102,126,234,0.08)')
                            : 'transparent',
                          border: ticked
                            ? `1px solid ${isUpdate ? 'rgba(239,108,0,0.35)' : 'rgba(102,126,234,0.25)'}`
                            : '1px solid transparent',
                          transition: 'all 0.15s ease',
                          '&:hover': { background: 'rgba(102,126,234,0.06)' },
                        }}>
                        <Checkbox size="small"
                          checked={ticked}
                          onChange={() => handleInsuranceToggle(insurance.id)}
                          onClick={e => e.stopPropagation()}
                          sx={{
                            p: 0.5,
                            color: isUpdate ? '#ef6c00' : '#667eea',
                            '&.Mui-checked': { color: isUpdate ? '#ef6c00' : '#667eea' },
                          }} />
                        <Box sx={{ ml: 0.5, flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#333' }}>
                            {insurance.name}
                          </Typography>
                          {/*  What is already there, named. "Exists" on its own
                               told you nothing about what re-applying would
                               overwrite. */}
                          {isUpdate && (
                            <Typography variant="caption" sx={{ color: '#ef6c00', fontSize: '0.68rem' }}>
                              has {ageCat}{currentCode ? ` · ${currentCode}` : ''} — will be updated
                            </Typography>
                          )}
                        </Box>

                        {/*  THE SCHEME'S OWN CODE. Only once ticked — an editable
                             box on every one of seventeen rows is noise, and a code
                             typed against a scheme that is not selected would be
                             silently discarded on Apply.

                             Falls back to the shared code above, and
                             stopPropagation so typing does not toggle the row. */}
                        {ticked && (
                          <TextField
                            size="small"
                            value={codeByProvider[insurance.id] ?? coreCode}
                            onChange={e => setCodeByProvider(prev => ({ ...prev, [insurance.id]: e.target.value }))}
                            onClick={e => e.stopPropagation()}
                            placeholder={coreCode || sourceRow.core || 'Core / G-DRG'}
                            inputProps={{ 'aria-label': `Code for ${insurance.name}` }}
                            sx={{
                              ml: 1, width: 148, flexShrink: 0,
                              '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.5, fontWeight: 600 },
                            }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {filteredProviders.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                  <Typography variant="body2" color="text.secondary">No providers found</Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0', background: '#fafafa', gap: 1 }}>
        <Button onClick={handleClose} startIcon={<ClearIcon />} variant="outlined" disabled={saving}
          sx={{ color: '#757575', borderColor: '#e0e0e0', '&:hover': { background: '#f5f5f5' } }}>
          Cancel
        </Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CopyIcon />}
          onClick={handleApply} disabled={saving || selectedCount === 0}
          sx={{
            background: 'linear-gradient(90deg,#667eea,#764ba2)', color: 'white', fontWeight: 700, px: 3,
            boxShadow: '0 4px 14px rgba(102,126,234,0.35)',
            '&:hover': { background: 'linear-gradient(90deg,#5568d3,#6a3f8f)', boxShadow: '0 6px 18px rgba(102,126,234,0.45)' },
            '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' },
          }}>
          {saving
            ? 'Applying...'
            : `Apply ${ageCat} to ${selectedCount} Provider${selectedCount !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────────────────────
   New-Diagnosis Dialog
───────────────────────────────────────────────────────────── */
const NewDiagnosisDialog = ({ open, onClose, insuranceProviders, loadingInsurance, onSaved }) => {
  const [saving, setSaving] = useState(false);

  // ── form state ────────────────────────────────────────────
  const [ageCategory, setAgeCategory] = useState('');
  const [diagnosisName, setDiagnosisName] = useState('');
  const [category, setCategory] = useState('');
  const [icdCode, setIcdCode] = useState('');
  const [corePackage, setCorePackage] = useState('');
  const [tariff, setTariff] = useState('');
  const [topUp, setTopUp] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [specialtyCode, setSpecialtyCode] = useState('');
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [errors, setErrors] = useState({});

  // ── existing values for the Category / Core Package dropdowns ─────────────
  // Fetched when the dialog opens rather than on mount, so the list reflects
  // anything added since the page was loaded. Failure is silent by design: the
  // fields degrade to plain text boxes rather than blocking a new diagnosis.
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [coreOptions, setCoreOptions] = useState([]);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [specialtyCodeOptions, setSpecialtyCodeOptions] = useState([]);
  // { lowercased specialty -> its code }, only where the pairing is unambiguous.
  const [specialtyMap, setSpecialtyMap] = useState({});
  // The code WE filled in last. Lets a later specialty change replace it while
  // never overwriting a code the operator typed themselves.
  const autoFilledCode = useRef('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setOptionsLoading(true);
    setOptionsError('');
    axios.get(diagnosisFieldOptions)
      .then(res => {
        if (cancelled) return;
        setCategoryOptions(res.data?.categories || []);
        setCoreOptions(res.data?.corePackages || []);
        setSpecialtyOptions(res.data?.specialties || []);
        setSpecialtyCodeOptions(res.data?.specialtyCodes || []);
        setSpecialtyMap(res.data?.specialtyMap || {});
      })
      .catch(err => {
        if (cancelled) return;
        // Surface it. A swallowed failure here looks identical to an empty
        // table, and the commonest cause is the backend not yet restarted on
        // the build that has /diagnosisFieldOptions - which reads as a 404.
        const status = err.response?.status;
        setOptionsError(
          status === 404
            ? 'endpoint not found - restart the API server on the latest build'
            : (err.response?.data?.message || err.message || 'request failed')
        );
        console.warn('[diagnosis] field options failed:', status, err.message);
      })
      .finally(() => { if (!cancelled) setOptionsLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

  const clearError = (field) => setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

  const handleInsuranceToggle = (id) => {
    setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
    clearError('insurance');
  };
  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { all[i.id] = true; });
    setSelectedInsurances(all);
    clearError('insurance');
  };
  const handleDeselectAll = () => setSelectedInsurances({});

  /* ── validation ──────────────────────────────────────────── */
  const validate = () => {
    const e = {};

    if (!ageCategory)
      e.ageCategory = 'Age Category is required';

    if (!diagnosisName.trim())
      e.diagnosisName = 'Diagnosis Name is required';

    if (!category.trim())
      e.category = 'Category is required';

    if (!icdCode.trim())
      e.icdCode = 'ICD Code is required';

    if (!corePackage.trim())
      e.corePackage = 'Core Package is required';

    // Tariff: required, must be a number ≥ 0
    if (tariff === '' || tariff === null || tariff === undefined)
      e.tariff = 'Tariff is required';
    else if (isNaN(parseFloat(tariff)) || parseFloat(tariff) < 0)
      e.tariff = 'Must be a valid number (0 or more)';

    // Top-up: required, must be a number ≥ 0
    if (topUp === '' || topUp === null || topUp === undefined)
      e.topUp = 'Top-up is required';
    else if (isNaN(parseFloat(topUp)) || parseFloat(topUp) < 0)
      e.topUp = 'Must be a valid number (0 or more)';

    if (!specialty.trim())
      e.specialty = 'Specialty is required';

    if (!specialtyCode.trim())
      e.specialtyCode = 'Specialty Code is required';

    if (selectedCount === 0)
      e.insurance = 'At least one insurance provider must be selected';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── save handler ────────────────────────────────────────── */
  const handleSave = async () => {
    if (!validate()) {
      const pending = {
        ...(!ageCategory ? { a: 'Age Category must be selected' } : {}),
        ...(!diagnosisName.trim() ? { b: 'Diagnosis Name must be entered' } : {}),
        ...(!category.trim() ? { c: 'Category must be entered' } : {}),
        ...(!icdCode.trim() ? { d: 'ICD Code must be entered' } : {}),
        ...(!corePackage.trim() ? { e: 'Core Package must be entered' } : {}),
        ...(tariff === '' ? { f: 'Tariff must be entered' } : {}),
        ...(topUp === '' ? { g: 'Top-up must be entered' } : {}),
        ...(!specialty.trim() ? { h: 'Specialty must be entered' } : {}),
        ...(!specialtyCode.trim() ? { i: 'Specialty Code must be entered' } : {}),
        ...(selectedCount === 0 ? { j: 'At least one insurance must be selected' } : {}),
      };
      await Swal.fire({
        title: 'Please complete all required fields',
        html: `<ul style="text-align:left;margin:0;padding-left:1.2em;">${Object.values(pending).map(m => `<li>${m}</li>`).join('')}</ul>`,
        icon: 'warning',
        confirmButtonColor: '#11998e',
      });
      return;
    }

    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        return {
          age_cat: ageCategory,
          name: diagnosisName.trim(),
          cat: category.trim(),
          icd: icdCode.trim(),
          core: corePackage.trim(),
          tariff: parseFloat(tariff),
          topup: parseFloat(topUp),
          spe: specialty.trim(),
          spe_code: specialtyCode.trim(),
          status: provider?.name || ''
        };
      });

      const response = await axios.post(saveNewDiagnosis, { rows });

      if (response.data.success) {
        await Swal.fire({
          title: 'Saved!',
          html: `<b>${diagnosisName}</b> saved with <b>${selectedCount}</b> insurance record${selectedCount !== 1 ? 's' : ''}.`,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        if (onSaved) onSaved();
        handleClose();
      } else {
        throw new Error(response.data.message || 'Save failed');
      }
    } catch (err) {
      await Swal.fire({
        title: 'Save Failed',
        text: err.response?.data?.message || err.message || 'An error occurred while saving',
        icon: 'error',
        confirmButtonColor: '#e53935',
      });
    } finally {
      setSaving(false);
    }
  };

  /* Selecting a specialty sets its code. Full stop.
   *
   * The first version only filled an empty box, or one it had filled itself,
   * so that a hand-typed code was never clobbered. In practice that produced
   * the opposite of helpful: pick a code by hand once, then change the
   * specialty, and the code stayed behind, silently disagreeing with it —
   * OBSTETRICS AND GYNECOLOGY showing MEDI. Being protective about a field the
   * specialty determines just left the two out of step.
   *
   * So the specialty now wins outright. The code stays editable afterwards,
   * and changing the specialty again simply sets it again.
   *
   * A specialty with no known code leaves the box alone rather than blanking
   * it — that is a brand-new specialty being typed, not a request to erase.
   */
  const handleSpecialtyChange = (next) => {
    setSpecialty(next);
    clearError('specialty');

    const code = specialtyMap[String(next || '').trim().toLowerCase()];
    if (!code) return;
    if (code === specialtyCode) { autoFilledCode.current = code; return; }

    autoFilledCode.current = code;
    setSpecialtyCode(code);
    clearError('specialtyCode');
  };

  // True while the code box still holds what the specialty put there, so the
  // field can say so instead of leaving the operator guessing where it came from.
  const codeWasAutoFilled =
    !!specialtyCode && specialtyCode === autoFilledCode.current;

  /* ── reset & close ───────────────────────────────────────── */
  const handleClose = () => {
    setAgeCategory(''); setDiagnosisName(''); setCategory('');
    setIcdCode(''); setCorePackage(''); setTariff(''); setTopUp('');
    setSpecialty(''); setSpecialtyCode('');
    autoFilledCode.current = '';
    setSelectedInsurances({}); setInsuranceSearch('');
    setErrors({});
    onClose();
  };

  const filteredProviders = insuranceProviders.filter(i =>
    i.name?.toLowerCase().includes(insuranceSearch.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' } }}>

      <DialogTitle sx={{ background: 'linear-gradient(135deg,#11998e,#38ef7d)', color: 'white', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DiagnosisIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>New Diagnosis</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>Configure diagnosis details and assign insurance providers</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ minHeight: 520 }} columns={{ xs: 4, sm: 8, md: 12 }}>

          {/* ── LEFT: diagnosis fields ───────────────────────── */}
          <Grid item xs={12} md={6} size={6} sx={{ p: 3, borderRight: { md: '1px solid #e8eaf6' }, background: '#fafbff' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3949ab', mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
              Diagnosis Details
            </Typography>

            {/* Age Category */}
            <FormControl component="fieldset" sx={{ mb: 1.5, width: '100%' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: errors.ageCategory ? '#e53935' : '#555', mb: 0.5, display: 'block' }}>
                Age Category *
              </Typography>
              <RadioGroup row value={ageCategory}
                onChange={e => { setAgeCategory(e.target.value); clearError('ageCategory'); }}>
                {['Adult', 'Child', 'Both'].map(v => (
                  <FormControlLabel key={v} value={v}
                    control={<Radio size="small" sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                    label={<Typography variant="body2">{v}</Typography>} />
                ))}
              </RadioGroup>
              {errors.ageCategory && <Typography variant="caption" sx={{ color: '#e53935', fontSize: '0.7rem' }}>{errors.ageCategory}</Typography>}
            </FormControl>

            <Divider sx={{ my: 1.5 }} />

            {/* Diagnosis Name */}
            <TextField fullWidth size="small" label="Diagnosis Name *" value={diagnosisName}
              onChange={e => { setDiagnosisName(e.target.value); clearError('diagnosisName'); }}
              error={!!errors.diagnosisName} helperText={errors.diagnosisName} sx={{ mb: 2 }} />

            {/* Category — pick an existing one or type a new one */}
            <LookupField
              label="Category *"
              value={category}
              onChange={v => { setCategory(v); clearError('category'); }}
              options={categoryOptions}
              loading={optionsLoading}
              loadError={optionsError}
              error={!!errors.category}
              helperText={errors.category}
              placeholder="e.g., CARDIAC DISEASES, MALNUTRITION"
              sx={{ mb: 2 }}
            />

            {/* ICD Code */}
            <TextField fullWidth size="small" label="ICD Code *" value={icdCode}
              onChange={e => { setIcdCode(e.target.value); clearError('icdCode'); }}
              error={!!errors.icdCode} helperText={errors.icdCode} sx={{ mb: 2 }}
              placeholder="e.g., I50.1, E43" />

            {/* Core Package — pick an existing one or type a new one */}
            <LookupField
              label="Core Package/Code *"
              value={corePackage}
              onChange={v => { setCorePackage(v); clearError('corePackage'); }}
              options={coreOptions}
              loading={optionsLoading}
              loadError={optionsError}
              error={!!errors.corePackage}
              helperText={errors.corePackage}
              placeholder="e.g., MEDI07A, PAED10C"
              sx={{ mb: 2 }}
            />

            {/* Tariff + Top-up */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6} size={6}>
                <TextField fullWidth size="small" label="Tariff GH¢ *" type="number"
                  value={tariff}
                  onChange={e => { setTariff(e.target.value); clearError('tariff'); }}
                  error={!!errors.tariff} helperText={errors.tariff}
                  inputProps={{ min: 0, step: '0.01' }} />
              </Grid>
              <Grid item xs={12} md={6} size={6}>
                <TextField fullWidth size="small" label="Top-up GH¢ *" type="number"
                  value={topUp}
                  onChange={e => { setTopUp(e.target.value); clearError('topUp'); }}
                  error={!!errors.topUp} helperText={errors.topUp}
                  inputProps={{ min: 0, step: '0.01' }} />
              </Grid>
            </Grid>

            {/* Specialty — pick an existing one or type a new one.
                Choosing one fills in the code below. */}
            <LookupField
              label="Specialty *"
              value={specialty}
              onChange={handleSpecialtyChange}
              options={specialtyOptions}
              loading={optionsLoading}
              loadError={optionsError}
              error={!!errors.specialty}
              helperText={errors.specialty}
              placeholder="e.g., Paediatrics, Orthopaedics"
              sx={{ mb: 2 }}
            />

            {/* Specialty Code — set by the specialty above, still editable and
                still open to a brand-new value. */}
            <LookupField
              label="Specialty Code *"
              value={specialtyCode}
              onChange={v => { setSpecialtyCode(v); clearError('specialtyCode'); }}
              options={specialtyCodeOptions}
              loading={optionsLoading}
              loadError={optionsError}
              error={!!errors.specialtyCode}
              helperText={
                errors.specialtyCode ||
                (codeWasAutoFilled ? 'Filled from the specialty above — change it if you need to' : '')
              }
              placeholder="e.g., PAED, ORTH"
            />
          </Grid>

          {/* ── RIGHT: insurance checkboxes ────────────────── */}
          <Grid item xs={12} md={6} size={6} sx={{ p: 3, display: 'flex', flexDirection: 'column', background: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box>
                <Typography variant="subtitle2"
                  sx={{ fontWeight: 700, color: errors.insurance ? '#e53935' : '#3949ab', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
                  Insurance Providers *
                </Typography>
                <Typography variant="caption" color={errors.insurance ? 'error' : 'text.secondary'}>
                  {selectedCount} of {insuranceProviders.length} selected
                </Typography>
              </Box>
              {selectedCount > 0 && (
                <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                  label={`${selectedCount} selected`}
                  sx={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }} />
              )}
            </Box>

            {errors.insurance && (
              <Typography variant="caption" sx={{ color: '#e53935', fontSize: '0.7rem', mb: 0.5 }}>
                {errors.insurance}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, mt: 0.5 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll}
                sx={{ borderColor: '#667eea', color: '#667eea', fontSize: '0.72rem', py: 0.4 }}>
                Select All
              </Button>
              <Button size="small" variant="outlined" onClick={handleDeselectAll}
                sx={{ borderColor: '#667eea', color: '#667eea', fontSize: '0.72rem', py: 0.4 }}>
                Deselect All
              </Button>
            </Box>

            <TextField fullWidth size="small" placeholder="Search insurance..."
              value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)} sx={{ mb: 1.5 }}
              InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#9e9e9e' }} /></InputAdornment>) }} />

            <Box sx={{
              flex: 1, overflowY: 'auto',
              border: `1px solid ${errors.insurance ? '#e53935' : '#e8eaf6'}`,
              borderRadius: 2, p: 1, minHeight: 280, maxHeight: 320,
              transition: 'border-color 0.2s',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { background: '#c5cae9', borderRadius: '10px' },
            }}>
              {loadingInsurance ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={28} />
                </Box>
              ) : filteredProviders.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">No providers found</Typography>
                </Box>
              ) : (
                filteredProviders.map(insurance => (
                  <Box key={insurance.id} onClick={() => handleInsuranceToggle(insurance.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1,
                      cursor: 'pointer', mb: 0.5,
                      background: selectedInsurances[insurance.id] ? 'rgba(102,126,234,0.08)' : 'transparent',
                      border: selectedInsurances[insurance.id] ? '1px solid rgba(102,126,234,0.25)' : '1px solid transparent',
                      transition: 'all 0.15s ease',
                      '&:hover': { background: 'rgba(102,126,234,0.06)' },
                    }}>
                    <Checkbox size="small"
                      checked={selectedInsurances[insurance.id] || false}
                      onChange={() => handleInsuranceToggle(insurance.id)}
                      onClick={e => e.stopPropagation()}
                      sx={{ p: 0.5, color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />
                    <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem', color: '#333' }}>
                      {insurance.name}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0', background: '#fafafa', gap: 1 }}>
        <Button onClick={handleClose} startIcon={<ClearIcon />} variant="outlined" disabled={saving}
          sx={{ color: '#757575', borderColor: '#e0e0e0', '&:hover': { background: '#f5f5f5' } }}>
          Cancel
        </Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave} disabled={saving}
          sx={{
            background: 'linear-gradient(90deg,#11998e,#38ef7d)', color: 'white', fontWeight: 700, px: 3,
            boxShadow: '0 4px 14px rgba(17,153,142,0.35)',
            '&:hover': { background: 'linear-gradient(90deg,#0e8a7f,#2fd469)', boxShadow: '0 6px 18px rgba(17,153,142,0.45)' },
            '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' },
          }}>
          {saving ? 'Saving...' : 'Save Diagnosis'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────── */
const Diagnosis = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [loadingDiagnoses, setLoadingDiagnoses] = useState(false);
  const [loadingDiagnosisInsurances, setLoadingDiagnosisInsurances] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState(null);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);
  const [diagnosisInsuranceRows, setDiagnosisInsuranceRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkApplyDialogOpen, setBulkApplyDialogOpen] = useState(false);
  const [sourceRowForBulkApply, setSourceRowForBulkApply] = useState(null);

  // ── "Apply to many" toolbar state ─────────────────────────
  const [bulkValues, setBulkValues]     = useState({});
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const loadInsuranceProviders = async () => {
    setLoadingInsurance(true); setError(null);
    try {
      const res = await axios.get(loadInsurance);
      if (res.data.success) setInsuranceProviders(res.data.results);
      else setError('Failed to load insurance providers');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load insurance providers');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load insurance providers', icon: 'error' });
    } finally { setLoadingInsurance(false); }
  };

  const loadDiagnosesFromBackend = async () => {
    setLoadingDiagnoses(true); setError(null);
    try {
      const res = await axios.get(loadDiagnoses);
      if (res.data.success) setDiagnoses(res.data.results);
      else setError('Failed to load diagnoses');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load diagnoses');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load diagnoses', icon: 'error' });
    } finally { setLoadingDiagnoses(false); }
  };

  const loadDiagnosisInsurancesForDisplay = async (diagnosisId, diagnosisName) => {
    setLoadingDiagnosisInsurances(true); setDiagnosisInsuranceRows([]); setSelectedRowIds([]); setBulkValues({});
    try {
      const res = await axios.post(getDiagnosisInsurances, { diagnosisId, diagnosisName });
      if (res.data.success) setDiagnosisInsuranceRows(res.data.results);
    } catch (err) {
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load diagnosis insurances', icon: 'error' });
    } finally { setLoadingDiagnosisInsurances(false); }
  };

  const handleUpdateRow = async (row) => {
    if (!row.tariff || !row.topup || !row.age_cat) {
      await Swal.fire({ title: 'Validation Error', text: 'Please fill in all required fields (Tariff, Top-up, Age Category)', icon: 'warning' }); return;
    }
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot update.', icon: 'error' }); return; }
    setUpdatingRowId(row.id);
    try {
      const res = await axios.post(updateInsuredDiagnosis, {
        id: row.id,
        tariff: parseFloat(row.tariff),
        topup: parseFloat(row.topup),
        age_cat: row.age_cat,
        icd: row.icd || '',
        core: row.core || '',
        spe: row.spe || '',
        spe_code: row.spe_code || ''
      });
      if (res.data.success) {
        await Swal.fire({ title: 'Success!', text: `Successfully updated ${row.status}`, icon: 'success', timer: 2000, showConfirmButton: false });
        if (selectedDiagnosis) await loadDiagnosisInsurancesForDisplay(selectedDiagnosis.id, selectedDiagnosis.name);
      } else throw new Error(res.data.message || 'Update failed');
    } catch (err) {
      await Swal.fire({ title: 'Update Failed', text: err.response?.data?.message || err.message || 'Failed to update', icon: 'error' });
    } finally { setUpdatingRowId(null); }
  };

  const handleDeleteRow = async (row) => {
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot delete.', icon: 'error' }); return; }
    const result = await Swal.fire({
      title: 'Confirm Delete',
      html: `Are you sure you want to delete the record for <strong>${row.status || 'this entry'}</strong>?<br/><span style="color:#e53935;font-size:0.9rem;">This action cannot be undone.</span>`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#e53935', cancelButtonColor: '#9e9e9e',
      confirmButtonText: 'Yes, Delete', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setDeletingRowId(row.id);
    try {
      const res = await axios.post(deleteInsuredDiagnosis, { id: row.id });
      if (res.data.success) {
        await Swal.fire({ title: 'Deleted!', text: `Record for "${row.status}" has been deleted.`, icon: 'success', timer: 2000, showConfirmButton: false });
        setDiagnosisInsuranceRows(prev => prev.filter(r => r.id !== row.id));
        setSelectedRowIds(prev => prev.filter(id => id !== row.id));
      } else throw new Error(res.data.message || 'Delete failed');
    } catch (err) {
      await Swal.fire({ title: 'Delete Failed', text: err.response?.data?.message || err.message || 'Failed to delete', icon: 'error' });
    } finally { setDeletingRowId(null); }
  };

  const handleRowSelectionToggle = (rowId) => {
    setSelectedRowIds(prev => 
      prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
    );
  };

  const handleSelectAllRows = () => {
    if (selectedRowIds.length === diagnosisInsuranceRows.length) {
      setSelectedRowIds([]);
    } else {
      setSelectedRowIds(diagnosisInsuranceRows.map(r => r.id));
    }
  };

  const handleBulkApply = () => {
    if (selectedRowIds.length !== 1) {
      Swal.fire({
        title: 'Selection Required',
        text: 'Please select exactly ONE row to use as template for bulk insurance assignment.',
        icon: 'info',
        confirmButtonColor: '#11998e',
      });
      return;
    }

    const sourceRow = diagnosisInsuranceRows.find(r => r.id === selectedRowIds[0]);
    if (!sourceRow) return;

    setSourceRowForBulkApply(sourceRow);
    setBulkApplyDialogOpen(true);
  };

  /* ── "Apply to many" ────────────────────────────────────────
     Type the values once, tick the schemes, apply/delete in one go.
  ─────────────────────────────────────────────────────────────*/
  const handleBulkFieldChange = (key, value) =>
    setBulkValues(prev => ({ ...prev, [key]: value }));

  const filledBulkFields = () => Object.fromEntries(
    APPLY_TO_MANY_FIELDS
      .filter(f => String(bulkValues[f.key] ?? '').trim() !== '')
      .map(f => [f.key, String(bulkValues[f.key]).trim()])
  );

  const handleApplyToMany = async () => {
    const fields = filledBulkFields();
    if (selectedRowIds.length === 0 || Object.keys(fields).length === 0) return;

    const tickedRows = diagnosisInsuranceRows.filter(r => selectedRowIds.includes(r.id));
    const confirm = await Swal.fire({
      title: `Apply to ${selectedRowIds.length} scheme${selectedRowIds.length !== 1 ? 's' : ''}?`,
      html: `
        <div style="text-align:left;font-size:0.9rem;">
          <div style="margin-bottom:.5em;">These values will be written to every ticked scheme:</div>
          <ul style="margin:0 0 .75em;padding-left:1.2em;">
            ${APPLY_TO_MANY_FIELDS.filter(f => fields[f.key] !== undefined)
              .map(f => `<li><b>${f.label}</b>: ${fields[f.key]}</li>`).join('')}
          </ul>
          <div style="color:#607d8b;">Schemes: ${tickedRows.map(r => r.status || '—').join(', ')}</div>
        </div>`,
      icon: 'question', showCancelButton: true,
      confirmButtonColor: '#11998e', cancelButtonColor: '#9e9e9e',
      confirmButtonText: 'Yes, apply', cancelButtonText: 'Cancel',
    });
    if (!confirm.isConfirmed) return;

    setBulkApplying(true);
    try {
      const res = await axios.post(bulkUpdateInsuredDiagnosis, { ids: selectedRowIds, fields });
      if (res.data.success) {
        await Swal.fire({ title: 'Applied!', text: res.data.message, icon: 'success', timer: 2200, showConfirmButton: false });
        setBulkValues({});
        if (selectedDiagnosis) await loadDiagnosisInsurancesForDisplay(selectedDiagnosis.id, selectedDiagnosis.name);
      } else throw new Error(res.data.message || 'Apply failed');
    } catch (err) {
      await Swal.fire({ title: 'Apply Failed', text: err.response?.data?.message || err.message || 'Failed to apply', icon: 'error' });
    } finally { setBulkApplying(false); }
  };

  const handleDeleteTicked = async () => {
    if (selectedRowIds.length === 0) return;

    const tickedRows = diagnosisInsuranceRows.filter(r => selectedRowIds.includes(r.id));
    const confirm = await Swal.fire({
      title: `Delete ${selectedRowIds.length} scheme${selectedRowIds.length !== 1 ? 's' : ''}?`,
      html: `
        <div style="text-align:left;font-size:0.9rem;">
          <div style="margin-bottom:.5em;">These records will be removed from
            <b>${selectedDiagnosis?.name || 'this diagnosis'}</b>:</div>
          <ul style="margin:0 0 .75em;padding-left:1.2em;">
            ${tickedRows.map(r => `<li>${r.status || '—'}</li>`).join('')}
          </ul>
          <div style="color:#e53935;">This action cannot be undone.</div>
        </div>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#e53935', cancelButtonColor: '#9e9e9e',
      confirmButtonText: 'Yes, delete', cancelButtonText: 'Cancel',
    });
    if (!confirm.isConfirmed) return;

    setBulkDeleting(true);
    try {
      const res = await axios.post(bulkDeleteInsuredDiagnosis, { ids: selectedRowIds });
      if (res.data.success) {
        await Swal.fire({ title: 'Deleted!', text: res.data.message, icon: 'success', timer: 2200, showConfirmButton: false });
        setDiagnosisInsuranceRows(prev => prev.filter(r => !selectedRowIds.includes(r.id)));
        setSelectedRowIds([]);
      } else throw new Error(res.data.message || 'Delete failed');
    } catch (err) {
      await Swal.fire({ title: 'Delete Failed', text: err.response?.data?.message || err.message || 'Failed to delete', icon: 'error' });
    } finally { setBulkDeleting(false); }
  };

  useEffect(() => { loadInsuranceProviders(); loadDiagnosesFromBackend(); }, []);

  const handleDiagnosisClick = async (diagnosis) => {
    if (selectedDiagnosis?.id === diagnosis.id) { 
      setSelectedDiagnosis(null); 
      setDiagnosisInsuranceRows([]); 
      setSelectedRowIds([]);
      return; 
    }
    setSelectedDiagnosis(diagnosis);
    await loadDiagnosisInsurancesForDisplay(diagnosis.id, diagnosis.name);
  };

  const handleRowChange = (idx, field, value) =>
    setDiagnosisInsuranceRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  //  Scheme AND age, because that pair is the identity of a tariff row. Passing
  //  names alone made every scheme holding the adult row look unavailable for the
  //  child one, which is the bug this fixes.
  const existingRows = diagnosisInsuranceRows.map(r => ({
    status: r.status,
    age_cat: r.age_cat,
    //  Carried so a row about to be overwritten can name the code it currently
    //  holds — "has Child · PAED31C — will be updated" rather than a bare
    //  "Exists" that says nothing about what is at stake.
    core: r.core,
  }));

  return (
    <>
      <Paper elevation={8} sx={{ margin: '0 auto', borderRadius: 3, overflow: 'hidden', background: '#ffffff' }}>
        {/* Header */}
        <Box sx={{ background: 'linear-gradient(135deg,#11998e,#38ef7d)', color: 'white', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '0.5px', textShadow: '1px 2px 4px rgba(0,0,0,0.15)' }}>
            Diagnosis Tariff Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="contained" onClick={loadDiagnosesFromBackend} disabled={loadingDiagnoses}
              startIcon={loadingDiagnoses ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              size="small" sx={{ background: 'rgba(255,255,255,0.2)', '&:hover': { background: 'rgba(255,255,255,0.3)' } }}>
              {loadingDiagnoses ? 'Loading...' : 'Reload Diagnoses'}
            </Button>
            <Button variant="contained" onClick={loadInsuranceProviders} disabled={loadingInsurance}
              startIcon={loadingInsurance ? <CircularProgress size={16} color="inherit" /> : <CloudDownload />}
              size="small" sx={{ background: 'rgba(255,255,255,0.2)', '&:hover': { background: 'rgba(255,255,255,0.3)' } }}>
              {loadingInsurance ? 'Loading...' : 'Reload Insurance'}
            </Button>
            <Tooltip title="Add a new diagnosis">
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)} size="small"
                sx={{ background: 'rgba(255,255,255,0.95)', color: '#11998e', fontWeight: 700, '&:hover': { background: '#fff', boxShadow: '0 4px 14px rgba(0,0,0,0.2)' } }}>
                New Diagnosis
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
          <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>

            {/* Left: diagnosis list */}
            <Grid item xs={12} md={4} size={4}>
              <Paper elevation={0} sx={{ p: 2.5, border: '2px solid #e0e0e0', borderRadius: 2, background: 'linear-gradient(to bottom,#fff8f3,#ffffff)', height: 580, display: 'flex', flexDirection: 'column' }}>
                <TextField fullWidth size="small" placeholder="Search diagnoses..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)} sx={{ mb: 2 }}
                  InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#667eea' }} /></InputAdornment>) }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e' }}>Available Diagnoses</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loadingDiagnoses && <CircularProgress size={16} />}
                    <Chip size="small" label={`${diagnoses.length} total`} sx={{ fontSize: '0.68rem', background: '#e8eaf6', color: '#3949ab' }} />
                  </Box>
                </Box>
                {selectedDiagnosis && (
                  <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, background: 'linear-gradient(90deg,rgba(102,126,234,0.1),rgba(56,239,125,0.08))', border: '1px solid rgba(102,126,234,0.2)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#11998e' }} />
                    <Typography variant="caption" sx={{ color: '#3949ab', fontWeight: 600 }}>Selected: {selectedDiagnosis.name}</Typography>
                    <IconButton size="small" onClick={() => { setSelectedDiagnosis(null); setDiagnosisInsuranceRows([]); setSelectedRowIds([]); }} sx={{ ml: 'auto', p: 0.2 }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                )}
                <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#c5cae9', borderRadius: '10px' } }}>
                  {loadingDiagnoses ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
                  ) : diagnoses.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 1 }}>
                      <DiagnosisIcon sx={{ fontSize: 40, color: '#bdbdbd' }} />
                      <Typography variant="body2" color="text.secondary">No diagnoses found. Click "Reload Diagnoses".</Typography>
                    </Box>
                  ) : (
                    diagnoses.filter(d => d.name?.toLowerCase().includes(searchTerm.toLowerCase())).map(diagnosis => (
                      <Box key={diagnosis.id} onClick={() => handleDiagnosisClick(diagnosis)}
                        sx={{ display: 'flex', alignItems: 'center', mb: 0.75, p: 1.25, borderRadius: 1.5, cursor: 'pointer', transition: 'all 0.18s ease', border: selectedDiagnosis?.id === diagnosis.id ? '1.5px solid #667eea' : '1.5px solid transparent', background: selectedDiagnosis?.id === diagnosis.id ? 'rgba(102,126,234,0.08)' : 'transparent', '&:hover': { background: 'rgba(102,126,234,0.05)', transform: 'translateX(3px)', borderColor: 'rgba(102,126,234,0.3)' } }}>
                        <DiagnosisIcon sx={{ fontSize: 16, mr: 1, color: selectedDiagnosis?.id === diagnosis.id ? '#667eea' : '#bdbdbd', transition: 'color 0.18s' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', color: selectedDiagnosis?.id === diagnosis.id ? '#3949ab' : '#424242', fontWeight: selectedDiagnosis?.id === diagnosis.id ? 700 : 400 }}>
                          {diagnosis.name}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Right: editable records table */}
            <Grid item xs={12} md={8} size={8}>
              <Paper elevation={0} sx={{ border: '2px solid #e0e0e0', borderRadius: 2, background: '#fff', height: 580, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e8eaf6', background: '#f8f9ff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e' }}>Insurance Coverage Records</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedDiagnosis ? `Records for: ${selectedDiagnosis.name}` : 'Select a diagnosis to load records'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {selectedRowIds.length > 0 && (
                      <Chip 
                        size="small" 
                        label={`${selectedRowIds.length} selected`}
                        sx={{ background: '#e3f2fd', color: '#1976d2', fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    )}
                    {selectedDiagnosis && !loadingDiagnosisInsurances && (
                      <Chip icon={<InsuranceIcon sx={{ fontSize: '14px !important' }} />}
                        label={`${diagnosisInsuranceRows.length} record${diagnosisInsuranceRows.length !== 1 ? 's' : ''}`} size="small"
                        sx={{ background: diagnosisInsuranceRows.length > 0 ? '#e8f5e9' : '#fce4ec', color: diagnosisInsuranceRows.length > 0 ? '#2e7d32' : '#c62828', fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                  </Box>
                </Box>

                {/* Apply-to-many toolbar */}
                {selectedDiagnosis && diagnosisInsuranceRows.length > 0 && (
                  <ApplyToManyBar
                    entity="scheme"
                    ticked={selectedRowIds.length}
                    total={diagnosisInsuranceRows.length}
                    fields={APPLY_TO_MANY_FIELDS}
                    values={bulkValues}
                    onChange={handleBulkFieldChange}
                    onApply={handleApplyToMany}
                    onDelete={handleDeleteTicked}
                    applying={bulkApplying}
                    deleting={bulkDeleting}
                    onSelectAll={() => setSelectedRowIds(diagnosisInsuranceRows.map(r => r.id))}
                    onClearSelection={() => setSelectedRowIds([])}
                    copy={{
                      label: 'Copy one row to other schemes',
                      hint: 'Adds this diagnosis to schemes that do not have it yet',
                      onClick: handleBulkApply,
                      disabled: selectedRowIds.length !== 1,
                    }}
                  />
                )}

                {!selectedDiagnosis ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, opacity: 0.4 }}>
                    <InsuranceIcon sx={{ fontSize: 56, color: '#90caf9' }} />
                    <Typography variant="body2" color="text.secondary" textAlign="center">Click any diagnosis on the left<br />to load its insurance records</Typography>
                  </Box>
                ) : loadingDiagnosisInsurances ? (
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box textAlign="center">
                      <CircularProgress size={36} sx={{ color: '#667eea' }} />
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#9e9e9e' }}>Loading records...</Typography>
                    </Box>
                  </Box>
                ) : diagnosisInsuranceRows.length === 0 ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, opacity: 0.5 }}>
                    <InsuranceIcon sx={{ fontSize: 44, color: '#ef9a9a' }} />
                    <Typography variant="body2" color="text.secondary">No insurance records found for this diagnosis.</Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto', '&::-webkit-scrollbar': { width: '6px', height: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#c5cae9', borderRadius: '10px' } }}>
                    <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ background: '#11998e', color: '#fff', width: 48, textAlign: 'center', py: 1.2, px: 0.5 }}>
                            <Checkbox 
                              size="small"
                              checked={selectedRowIds.length === diagnosisInsuranceRows.length && diagnosisInsuranceRows.length > 0}
                              indeterminate={selectedRowIds.length > 0 && selectedRowIds.length < diagnosisInsuranceRows.length}
                              onChange={handleSelectAllRows}
                              sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' }, p: 0.5 }}
                            />
                          </TableCell>
                          {['#', 'Insurance', 'Tariff (GH¢)', 'Top-up', 'Age', 'ICD Code', 'Core', 'Specialty', 'Code', 'Actions'].map(label => (
                            <TableCell key={label} sx={{ background: '#11998e', color: '#fff', fontWeight: 600, fontSize: '0.72rem', letterSpacing: '0.3px', borderBottom: 'none', py: 1.2, whiteSpace: 'nowrap', ...(label === '#' && { width: 36, textAlign: 'center' }), ...(label === 'Actions' && { width: 180, textAlign: 'center' }) }}>
                              {label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {diagnosisInsuranceRows.map((row, idx) => (
                          <EditableRow 
                            key={row.id || idx} 
                            row={row} 
                            idx={idx}
                            selected={selectedRowIds.includes(row.id)}
                            onSelectToggle={handleRowSelectionToggle}
                            onChange={(field, value) => handleRowChange(idx, field, value)}
                            onUpdate={handleUpdateRow} 
                            onDelete={handleDeleteRow}
                            updating={updatingRowId === row.id} 
                            deleting={deletingRowId === row.id} 
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <NewDiagnosisDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        insuranceProviders={insuranceProviders}
        loadingInsurance={loadingInsurance}
        onSaved={loadDiagnosesFromBackend}
      />

      <BulkApplyDialog
        open={bulkApplyDialogOpen}
        onClose={() => {
          setBulkApplyDialogOpen(false);
          setSourceRowForBulkApply(null);
        }}
        sourceRow={sourceRowForBulkApply}
        insuranceProviders={insuranceProviders}
        loadingInsurance={loadingInsurance}
        onSaved={() => {
          if (selectedDiagnosis) {
            loadDiagnosisInsurancesForDisplay(selectedDiagnosis.id, selectedDiagnosis.name);
          }
        }}
        existingRows={existingRows}
      />
    </>
  );
};

export default Diagnosis;





